import { apiRequest, queryString } from "./http";
import type {
  AuthSession,
  CurrentUserResponse,
  SessionListResponse,
  SignInPayload,
  SignInResponse,
} from "./generated/openapi";

export type {
  AuthProfile,
  AuthSession,
  AuthUser,
  CurrentUserResponse,
  SessionListResponse,
  SessionRecord,
  SignInPayload,
  SignInResponse,
} from "./generated/openapi";

// One-way cleanup for browser-readable credentials left by older releases.
// Current authentication state exists only in the server-issued HttpOnly cookie.
for (const key of ["ct_portal_session", "sb-wyzaqkhqlkzyrlwsuoyp-auth-token"]) {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // Storage may be unavailable in hardened browser contexts.
  }
}

export const authApi = {
  getMethods: (signal?: AbortSignal) => apiRequest<{
    providers: { provider: "google" | "microsoft"; configured: boolean }[];
    email_configured: boolean;
  }>("/api/auth/providers/status", { signal }),
  providerSignInUrl: (
    provider: "microsoft" | "google",
    returnTo?: string,
    invitationToken?: string,
  ) => {
    const query = queryString({
      return_to: returnTo,
      invitation_token: invitationToken,
    });
    return `/api/auth/providers/${provider}/start${query}`;
  },
  signIn: (email: string, password: string) =>
    apiRequest<SignInResponse>("/api/auth/sign-in", {
      method: "POST",
      body: JSON.stringify({ email, password } satisfies SignInPayload),
    }),

  signOut: () => apiRequest("/api/auth/sign-out", { method: "POST" }),

  getCurrentUser: (signal?: AbortSignal) =>
    apiRequest<CurrentUserResponse>("/api/me", { signal }),

  getSession: () => apiRequest<{ session: AuthSession }>("/api/auth/session"),

  listSessions: () => apiRequest<SessionListResponse>("/api/auth/sessions"),

  revokeSession: (id: string) =>
    apiRequest<{ ok: boolean; revoked_current: boolean }>(
      `/api/auth/sessions/${id}`,
      { method: "DELETE" },
    ),

  revokeOtherSessions: () =>
    apiRequest<{ ok: boolean; revoked: number }>(
      "/api/auth/sessions/revoke-others",
      { method: "POST" },
    ),

  requestPasswordReset: (email: string, invitationToken?: string) =>
    apiRequest<{ message: string }>("/api/auth/password-reset", {
      method: "POST",
      body: JSON.stringify({ email, invitation_token: invitationToken }),
    }),

  completePassword: (token: string, password: string) =>
    apiRequest<{ ok: boolean }>("/api/auth/complete-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),

  initialPassword: (newPassword: string) => apiRequest<{ ok: boolean }>("/api/auth/initial-password", {
    method: "POST", body: JSON.stringify({ new_password: newPassword }),
  }),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiRequest<{ ok: boolean }>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),
};
